import { useEffect, useRef } from 'react';

/**
 * Escape-to-dismiss for modal layers.
 *
 * Overlays stack several deep here — a lens detail modal can have a sample
 * lightbox or an engagement sub-modal on top of it — so a plain per-modal
 * `keydown` listener would collapse every open layer with one keypress.
 * Instead every open layer registers with the shared LIFO stack below and only
 * the top entry receives the key: Escape peels one layer off at a time.
 *
 * Two rules keep the stack matching what's actually on screen:
 *
 * 1. `onClose` is read through a ref, so passing an inline arrow does not
 *    re-register the layer on every render — a re-registration would push a
 *    parent back above its own child and make Escape close the wrong one.
 * 2. Call the hook once per layer, outermost first, in the component body.
 *    Effects run in call order, so declaration order is the stack order.
 *
 * A layer that must not be dismissed right now (a generation in flight, a form
 * mid-submit) still calls the hook and no-ops inside `onClose`. That swallows
 * the key instead of letting it fall through to the layer underneath — the
 * same guard the existing backdrop-click handlers already use:
 *
 *   useEscapeToClose(showAddSampleModal, () => {
 *     if (!addSampleGenerating && !addSampleSaving) setShowAddSampleModal(false);
 *   });
 */

type Layer = () => void;

const stack: Layer[] = [];

function handleKeyDown(event: KeyboardEvent) {
  if (event.key !== 'Escape') return;
  // Something closer to the input already handled it — the inline cell editors
  // on the Localization and Categories pages call preventDefault() so Escape
  // cancels the edit without also closing the layer around it.
  if (event.defaultPrevented) return;
  // Mid-IME-composition Escape cancels the composition, not the modal.
  if (event.isComposing || event.keyCode === 229) return;
  const top = stack[stack.length - 1];
  if (!top) return;
  event.preventDefault();
  top();
}

export function useEscapeToClose(isOpen: boolean, onClose: () => void): void {
  const latest = useRef(onClose);
  useEffect(() => {
    latest.current = onClose;
  });

  useEffect(() => {
    if (!isOpen) return;
    const layer: Layer = () => latest.current();
    stack.push(layer);
    if (stack.length === 1) document.addEventListener('keydown', handleKeyDown);
    return () => {
      const index = stack.lastIndexOf(layer);
      if (index !== -1) stack.splice(index, 1);
      if (stack.length === 0) document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);
}
